import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTranslation } from 'react-i18next';

interface RichTextEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  savePrompt?: () => void;
  simpleText?: boolean;
  disableContentLengthLimit?: boolean;
  hideLabel?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  label,
  value,
  onChange,
  savePrompt,
  simpleText,
  disableContentLengthLimit = false,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const MAX_LENGTH = 1000;

  const handleChange = (content: string) => {
    if (content.length > MAX_LENGTH && !disableContentLengthLimit) {
      alert(t('common.max_length_reached', { max: MAX_LENGTH }));
      return;
    }
    onChange(content);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value;
    handleChange(content);
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
  ];

  return (
    <div className="mb-6">
      <style>
        {`
          .ql-editor, .simple-editor {
            min-height: 100px;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 12px;
            font-family: inherit;
          }
          .simple-editor:focus {
            outline: none;
            border-color: #9ecaed;
            box-shadow: 0 0 5px #9ecaed;
          }
        `}
      </style>
      {!hideLabel && <label className="block text-gray-700">{label}</label>}

      {simpleText ? (
        <textarea
          value={value}
          onChange={handleTextareaChange}
          className="simple-editor mt-2 w-full resize-y"
          rows={5}
        />
      ) : (
        <ReactQuill
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          className="mt-2 editor"
        />
      )}

      <div className="flex justify-between items-center mt-1">
        {!disableContentLengthLimit && (
          <div className="text-sm text-gray-500">
            {value.length}/{MAX_LENGTH} {t('common.characters')}
          </div>
        )}
        {savePrompt && value.trim().length > 20 && (
          <button
            onClick={savePrompt}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {t('common.save_prompt_for_later')}
          </button>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
